/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CATEGORY_META, timeAgo, initials } from '@/lib/utils-cat';
import { MessageCircle, Users } from 'lucide-react';
import { EmptyState } from '@/components/UIExtras';

const ChatListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get('/chat/conversations');
        setConversations(res.data || []);
      } catch {
        setConversations([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const groupConvos = conversations.filter(c => c.type === 'group');
  const privateConvos = conversations.filter(c => c.type === 'private');


  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-semibold tracking-tight">{t('chat')}</h1>
      {loading && <div className="text-sm text-muted-foreground">{t('loading')}</div>}

      {!loading && (
        <>
          <section>
            <h2 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">{t('groupChat')}</h2>
            <div className="space-y-2">
              {groupConvos.map((c) => {
                const cat = CATEGORY_META[c.category];
                const Icon = cat?.icon || Users;
                return (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/chat/${c.id}`)}
                    className="w-full text-left flex items-center gap-3 p-3 bg-white border border-border rounded-2xl hover:bg-secondary/60 transition"
                    data-testid={`chat-list-row-group-${c.category}`}
                  >
                    <div className="h-11 w-11 rounded-full flex items-center justify-center" style={{ backgroundColor: cat?.soft, color: cat?.textOnSoft }}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{c.title}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {c.last_message ? c.last_message.content : t('groupChatDesc')}
                      </div>
                    </div>
                    {c.last_message && (
                      <div className="text-[11px] text-muted-foreground">{timeAgo(c.last_message.created_at, t)}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">{t('privateChats')}</h2>
            {privateConvos.length === 0 ? (
              <EmptyState
                icon={<MessageCircle className="h-6 w-6" />}
                title={t('noConversations')}
                subtitle={t('startChat')}
              />
            ) : (
              <div className="space-y-2">
                {privateConvos.map((c) => {
                  const other = c.other_user;
                  const cat = other ? CATEGORY_META[other.category] : null;
                  return (
                    <button
                      key={c.id}
                      onClick={() => navigate(`/chat/${c.id}`)}
                      className="w-full text-left flex items-center gap-3 p-3 bg-white border border-border rounded-2xl hover:bg-secondary/60 transition"
                      data-testid="chat-list-row-private"
                    >
                      <Avatar className="h-11 w-11">
                        {other?.avatar_base64 && <AvatarImage src={other.avatar_base64} />}
                        <AvatarFallback style={{ backgroundColor: cat?.soft, color: cat?.textOnSoft }}>
                          {initials(other?.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{other?.full_name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.last_message ? c.last_message.content : (other?.specialty || '—')}
                        </div>
                      </div>
                      {c.last_message && (
                        <div className="text-[11px] text-muted-foreground">{timeAgo(c.last_message.created_at, t)}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default ChatListPage;
